import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  RmetCreditsDialog,
  RmetCreditsLink,
  RmetIntroCredits,
  RmetItemFooter,
} from './RmetCredits';

const PUBLIC_API_URL = `${process.env.REACT_APP_API_URL}/public`;

const publicFetch = async (body: Record<string, unknown>): Promise<unknown> => {
  const res = await fetch(PUBLIC_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error ?? 'Request failed');
  return json.output;
};

type Step =
  | 'loading'
  | 'closed'
  | 'passcode'
  | 'demographics'
  | 'test'
  | 'task'
  | 'submitting'
  | 'done'
  | 'error';

/** An item as it arrives from `public_start_rmet` — no answer key. */
interface ClientItem {
  n: number;
  options: [string, string, string, string];
}

interface Answer {
  n: number;
  choice: number;
  ms: number;
}

const imageUrl = (n: number): string => `/rmet/${String(n).padStart(2, '0')}.jpg`;

/**
 * Styling and handlers that make the task text uncopyable — a deterrent, not a
 * control: anyone with DevTools can still read it. The brief textarea must
 * never receive these: participants have to be able to select and edit what
 * they typed.
 */
const NO_SELECT_SX = {
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MsUserSelect: 'none',
  // Suppresses the iOS/Android long-press selection callout.
  WebkitTouchCallout: 'none',
} as const;

const swallow = (e: React.SyntheticEvent) => e.preventDefault();

const noCopyProps = {
  onCopy: swallow,
  onCut: swallow,
  onContextMenu: swallow,
  onDragStart: swallow,
} as const;

// ------------------------------------------------------------
// Resume support
//
// Progress is mirrored to localStorage after every answer so a refresh or an
// accidentally closed tab doesn't lose a part-finished test. It is per-browser
// and per-session, cleared on successful submit, and expires on its own so a
// stale run can't resurrect weeks later.
//
// The passcode is part of the saved state because the item bank has to be
// re-fetched (via `public_start_rmet`) to resume, and requiring participants to
// re-enter it would defeat the point. It is the session's shared group code,
// not a personal credential.
// ------------------------------------------------------------

/**
 * One advisory budget per phase: the 36 RMET items, then the prompting task —
 * one screen holding the situation, the team nomination and the brief. Its
 * budget is the protocol's 15-second nomination plus the 4:00 brief.
 *
 * Each is advisory — it turns red when it runs out but never advances a screen
 * or submits on its own, so nobody loses a half-typed brief. Each is measured
 * against wall-clock time from the moment its phase starts (the matching
 * `PhaseTimings` field), so a refresh or a few minutes on another tab still
 * count against it.
 */
const PHASE_BUDGETS: Partial<Record<Step, { seconds: number; startedAt: keyof PhaseTimings }>> = {
  test: { seconds: 10 * 60, startedAt: 'rmetStartedAt' },
  task: { seconds: 4 * 60 + 15, startedAt: 'promptsStartedAt' },
};

/** Steps a saved run can be resumed at. Anything else (e.g. a run saved by the
 *  retired three-task flow) resumes at the prompting task. */
const RESUMABLE_STEPS: Step[] = ['demographics', 'test', 'task'];

const MAX_NOMINATION_LENGTH = 200;

const formatClock = (totalSeconds: number): string => {
  const s = Math.max(0, Math.round(totalSeconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const STORAGE_PREFIX = 'rmet-progress:';
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

/** Wall-clock boundaries of each phase, as ISO strings. */
interface PhaseTimings {
  rmetStartedAt?: string;
  rmetEndedAt?: string;
  /** The prompting-task screen opens. */
  promptsStartedAt?: string;
  /** End of the separate nomination screen, start of the brief. Only set by
   *  runs from the two-screen flow; the single screen has no such boundary. */
  taskStartedAt?: string;
  promptsEndedAt?: string;
}

interface SavedProgress {
  savedAt: number;
  passcode: string;
  step: Step;
  index: number;
  answers: Answer[];
  demographics: Record<string, string>;
  nomination?: string;
  brief?: string;
  timings?: PhaseTimings;
}

const storageKey = (token: string) => `${STORAGE_PREFIX}${token}`;

const loadProgress = (token: string): SavedProgress | null => {
  try {
    const raw = localStorage.getItem(storageKey(token));
    if (!raw) return null;
    const saved = JSON.parse(raw) as SavedProgress;
    if (!saved?.passcode || Date.now() - saved.savedAt > STORAGE_TTL_MS) {
      localStorage.removeItem(storageKey(token));
      return null;
    }
    return saved;
  } catch {
    // Private browsing, cleared site data, or corrupt JSON — start fresh.
    return null;
  }
};

const saveProgress = (token: string, progress: Omit<SavedProgress, 'savedAt'>): void => {
  try {
    localStorage.setItem(storageKey(token), JSON.stringify({ ...progress, savedAt: Date.now() }));
  } catch {
    // Storage unavailable or full — resume simply won't be offered.
  }
};

const clearProgress = (token: string): void => {
  try {
    localStorage.removeItem(storageKey(token));
  } catch {
    /* nothing to do */
  }
};

/** Option value → i18n key suffix. The value stored in DynamoDB is the key suffix,
 *  so the data stays language-independent and can be relabelled later. */
const GENDERS = ['female', 'male', 'non_binary', 'other', 'undisclosed'];
const EDUCATIONS = ['secondary', 'bachelor', 'master', 'phd', 'other', 'undisclosed'];
const DEGREE_FIELDS = [
  'stem',
  'humanities',
  'social',
  'business',
  'health',
  'none',
  'other',
  'undisclosed',
];
const NATIVE_LANGUAGES = ['it', 'en', 'other', 'undisclosed'];

/**
 * The single advisory countdown for the run. It goes red and switches wording
 * once the budget is spent, but nothing advances on its own — the participant
 * is always the one who moves forward.
 */
const Countdown: React.FC<{ secondsLeft: number }> = ({ secondsLeft }) => {
  const { t } = useTranslation();
  const over = secondsLeft < 0;
  return (
    <Typography variant="caption" color={over ? 'error.main' : 'text.secondary'}>
      {over
        ? t('rmet.timer_over', { time: formatClock(-secondsLeft) })
        : t('rmet.timer_remaining', { time: formatClock(secondsLeft) })}
    </Typography>
  );
};

interface Props {
  sessionToken: string;
}

export const RmetPage: React.FC<Props> = ({ sessionToken }) => {
  const { t, i18n } = useTranslation();
  const theme = React.useMemo(() => createTheme({ palette: { mode: 'light' } }), []);

  const [step, setStep] = React.useState<Step>('loading');
  const [sessionLabel, setSessionLabel] = React.useState('');
  const [passcode, setPasscode] = React.useState('');
  const [errorMsg, setErrorMsg] = React.useState('');

  const [items, setItems] = React.useState<ClientItem[]>([]);
  const [index, setIndex] = React.useState(0);
  const [choice, setChoice] = React.useState<number | null>(null);
  const answersRef = React.useRef<Answer[]>([]);
  const itemShownAt = React.useRef<number>(0);

  const [age, setAge] = React.useState('');
  const [gender, setGender] = React.useState('');
  const [education, setEducation] = React.useState('');
  const [degreeField, setDegreeField] = React.useState('');
  const [nativeLanguage, setNativeLanguage] = React.useState('');

  const [nomination, setNomination] = React.useState('');
  const [brief, setBrief] = React.useState('');
  const [score, setScore] = React.useState<number | null>(null);
  const [resumed, setResumed] = React.useState(false);
  /** Seconds left on the current screen's advisory countdown; negative once over. */
  const [secondsLeft, setSecondsLeft] = React.useState(0);
  /** Set briefly when a paste into the prompt box is rejected, to explain why. */
  const [pasteBlocked, setPasteBlocked] = React.useState(false);
  const timingsRef = React.useRef<PhaseTimings>({});
  const [creditsOpen, setCreditsOpen] = React.useState(false);
  const openCredits = React.useCallback(() => setCreditsOpen(true), []);

  React.useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const { label, status, lang } = (await publicFetch({
          operation: 'public_get_rmet_session',
          sessionToken,
        })) as { label: string; status: string; lang?: string };
        if (cancelled) return;

        setSessionLabel(label);
        // The test runs in the language the admin picked for this session,
        // overriding whatever the visitor's browser has stored under `lang`.
        if (lang && lang !== i18n.language) await i18n.changeLanguage(lang);
        if (cancelled) return;

        if (status === 'closed') {
          setStep('closed');
          return;
        }

        const saved = loadProgress(sessionToken);
        if (!saved) {
          setStep('passcode');
          return;
        }

        // Re-fetch the item bank with the saved passcode. If the session has
        // since been closed or the passcode no longer works, fall back to the
        // passcode screen rather than failing outright.
        try {
          const out = (await publicFetch({
            operation: 'public_start_rmet',
            sessionToken,
            passcode: saved.passcode,
          })) as { items: ClientItem[] };
          if (cancelled) return;

          setItems(out.items);
          setPasscode(saved.passcode);
          setAge(saved.demographics.age ?? '');
          setGender(saved.demographics.gender ?? '');
          setEducation(saved.demographics.education ?? '');
          setDegreeField(saved.demographics.degreeField ?? '');
          setNativeLanguage(saved.demographics.nativeLanguage ?? '');
          setNomination(saved.nomination ?? '');
          setBrief(saved.brief ?? '');
          answersRef.current = saved.answers ?? [];
          // Restoring the original phase starts keeps the budgets honest: time
          // spent away from the page is not given back.
          timingsRef.current = saved.timings ?? {};
          setIndex(Math.min(saved.index ?? 0, out.items.length - 1));
          itemShownAt.current = performance.now();
          // `saved.step` comes from storage, so it may name a step this build
          // no longer has: 'nomination' is the two-screen flow's first screen,
          // whose clock already started.
          const savedStep = saved.step as string;
          const resumeAt: Step = RESUMABLE_STEPS.includes(saved.step) ? saved.step : 'task';
          // A run saved mid-way through the retired three-task flow starts the
          // prompting clock now rather than showing minutes of overtime.
          if (resumeAt === 'task' && !['task', 'submitting', 'nomination'].includes(savedStep)) {
            timingsRef.current.promptsStartedAt = new Date().toISOString();
          }
          setStep(resumeAt);
          setResumed(true);
        } catch {
          if (cancelled) return;
          clearProgress(sessionToken);
          setStep('passcode');
        }
      } catch {
        if (!cancelled) setStep('error');
      }
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [sessionToken, i18n]);

  // Mirror progress after every answer, demographic edit and keystroke.
  React.useEffect(() => {
    if (!RESUMABLE_STEPS.includes(step)) return;
    saveProgress(sessionToken, {
      passcode,
      step,
      index,
      answers: answersRef.current,
      demographics: { age, gender, education, degreeField, nativeLanguage },
      nomination,
      brief,
      timings: timingsRef.current,
    });
  }, [
    sessionToken,
    passcode,
    step,
    index,
    nomination,
    brief,
    age,
    gender,
    education,
    degreeField,
    nativeLanguage,
  ]);

  // Warm the next image while the participant is answering the current one.
  React.useEffect(() => {
    if (step !== 'test') return;
    const next = items[index + 1];
    if (next) new Image().src = imageUrl(next.n);
  }, [step, index, items]);

  const handleStart = async () => {
    if (!passcode.trim()) return;
    setErrorMsg('');
    try {
      const out = (await publicFetch({
        operation: 'public_start_rmet',
        sessionToken,
        passcode: passcode.trim(),
      })) as { items: ClientItem[] };
      setItems(out.items);
      setStep('demographics');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t('rmet.error_generic'));
    }
  };

  const handleBeginTest = () => {
    answersRef.current = [];
    setIndex(0);
    setChoice(null);
    itemShownAt.current = performance.now();
    // The RMET budget starts here, with the first item.
    timingsRef.current = { rmetStartedAt: new Date().toISOString() };
    setStep('test');
  };

  const handleNext = () => {
    if (choice === null) return;
    answersRef.current.push({
      n: items[index].n,
      choice,
      ms: Math.round(performance.now() - itemShownAt.current),
    });
    setChoice(null);
    if (index + 1 < items.length) {
      setIndex(index + 1);
      itemShownAt.current = performance.now();
    } else {
      // One instant closes the RMET phase and opens the prompting task.
      const now = new Date().toISOString();
      timingsRef.current.rmetEndedAt = now;
      timingsRef.current.promptsStartedAt = now;
      setStep('task');
    }
  };

  // The current phase's advisory countdown, derived from wall-clock time so a
  // refresh or time on another tab still counts. It never advances a screen or
  // submits on its own. While submitting, the brief's budget stays on screen.
  React.useEffect(() => {
    const budget = PHASE_BUDGETS[step === 'submitting' ? 'task' : step];
    const startedAt = budget && timingsRef.current[budget.startedAt];
    if (!budget || !startedAt) return;
    const start = Date.parse(startedAt);
    const tick = () => setSecondsLeft(budget.seconds - (Date.now() - start) / 1000);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [step]);

  const handleSubmit = async () => {
    setStep('submitting');
    setErrorMsg('');
    // Stamped before the request so it measures the participant's time, not
    // the round-trip. Re-stamped on retry after a failed submit.
    timingsRef.current.promptsEndedAt = new Date().toISOString();
    try {
      const out = (await publicFetch({
        operation: 'public_submit_rmet',
        sessionToken,
        passcode: passcode.trim(),
        demographics: { age, gender, education, degreeField, nativeLanguage },
        rmetAnswers: answersRef.current,
        rmetNomination: nomination,
        rmetBrief: brief,
        rmetTimings: timingsRef.current,
      })) as { score: number };
      // The run is banked server-side; drop the local copy so a later visit to
      // the same link starts fresh rather than resuming a finished test.
      clearProgress(sessionToken);
      setScore(out.score);
      setStep('done');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t('rmet.error_generic'));
      setStep('task');
    }
  };

  // Demographics are optional: every field may be left blank, and each select
  // offers an explicit empty choice so a mis-click can be undone.
  const renderSelect = (
    label: string,
    value: string,
    setValue: (v: string) => void,
    options: string[],
    keyPrefix: string
  ) => (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select value={value} label={label} onChange={(e) => setValue(e.target.value)}>
        <MenuItem value="">
          <em>{t('rmet.no_answer')}</em>
        </MenuItem>
        {options.map((o) => (
          <MenuItem key={o} value={o}>
            {t(`rmet.${keyPrefix}_${o}`)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const item = items[index];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="flex-start"
        justifyContent="center"
        bgcolor="background.default"
        p={2}
      >
        <Paper sx={{ p: 4, maxWidth: 640, width: '100%', my: 4 }}>
          <Typography variant="h5" fontWeight="bold">
            {t('rmet_credits.test_title')}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" mb={2}>
            {sessionLabel || t('rmet_credits.test_subtitle')}
          </Typography>

          {resumed && step !== 'done' && (
            <Alert severity="info" sx={{ mb: 2 }} onClose={() => setResumed(false)}>
              {t('rmet.resumed')}
            </Alert>
          )}

          {step === 'loading' && (
            <Box display="flex" justifyContent="center" mt={4}>
              <CircularProgress />
            </Box>
          )}

          {step === 'closed' && <Alert severity="warning">{t('rmet.error_closed')}</Alert>}

          {step === 'error' && <Alert severity="error">{t('rmet.error_session_not_found')}</Alert>}

          {step === 'passcode' && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2">{t('rmet.intro')}</Typography>
              <RmetIntroCredits onOpenCredits={openCredits} />
              {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
              <TextField
                label={t('rmet.passcode')}
                type="password"
                required
                fullWidth
                size="small"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleStart();
                }}
              />
              <Button variant="contained" onClick={handleStart} disabled={!passcode.trim()}>
                {t('rmet.start')}
              </Button>
            </Box>
          )}

          {step === 'demographics' && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="h6">{t('rmet.demographics_title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('rmet.demographics_hint')}
              </Typography>
              <TextField
                label={t('rmet.age')}
                type="number"
                fullWidth
                size="small"
                value={age}
                slotProps={{ htmlInput: { min: 16, max: 120 } }}
                onChange={(e) => setAge(e.target.value)}
              />
              {renderSelect(t('rmet.gender'), gender, setGender, GENDERS, 'gender')}
              {renderSelect(t('rmet.education'), education, setEducation, EDUCATIONS, 'education')}
              {renderSelect(
                t('rmet.degree_field'),
                degreeField,
                setDegreeField,
                DEGREE_FIELDS,
                'degree_field'
              )}
              {renderSelect(
                t('rmet.native_language'),
                nativeLanguage,
                setNativeLanguage,
                NATIVE_LANGUAGES,
                'native_language'
              )}
              <Typography variant="body2" color="text.secondary">
                {t('rmet.test_instructions')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('rmet.test_no_back')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('rmet.demographics_time_note')}
              </Typography>
              <Button variant="contained" onClick={handleBeginTest}>
                {t('rmet.start')}
              </Button>
            </Box>
          )}

          {step === 'test' && item && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="caption" color="text.secondary">
                    {t('rmet.progress', { current: index + 1, total: items.length })}
                  </Typography>
                  <Countdown secondsLeft={secondsLeft} />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(index / items.length) * 100}
                  sx={{ mt: 0.5 }}
                />
              </Box>

              <Box
                component="img"
                src={imageUrl(item.n)}
                alt={t('rmet.image_alt', { n: item.n })}
                sx={{ width: '100%', borderRadius: 1, display: 'block' }}
              />

              <RadioGroup
                value={choice === null ? '' : String(choice)}
                onChange={(e) => setChoice(Number(e.target.value))}
              >
                {item.options.map((opt, i) => (
                  <FormControlLabel key={i} value={String(i)} control={<Radio />} label={opt} />
                ))}
              </RadioGroup>

              <Button variant="contained" onClick={handleNext} disabled={choice === null}>
                {t('rmet.next')}
              </Button>

              <RmetItemFooter onOpenCredits={openCredits} />
            </Box>
          )}

          {(step === 'task' || step === 'submitting') && (
            // One screen, in order: situation, team nomination, brief. While
            // submitting it stays on screen, so an error can be retried
            // without losing what was typed.
            <Box display="flex" flexDirection="column" gap={2}>
              <Box sx={NO_SELECT_SX} {...noCopyProps}>
                <Typography variant="h6">{t('rmet.task_situation_title')}</Typography>
              </Box>

              <Countdown secondsLeft={secondsLeft} />

              <Box sx={NO_SELECT_SX} {...noCopyProps}>
                {(['task_situation_p1', 'task_situation_p2', 'task_situation_p3'] as const).map(
                  (key) => (
                    <Typography key={key} variant="body2" paragraph sx={{ mb: 1 }}>
                      {t(`rmet.${key}`)}
                    </Typography>
                  )
                )}
              </Box>

              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {t('rmet.nomination_title')}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('rmet.nomination_body')}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={nomination}
                  disabled={step === 'submitting'}
                  slotProps={{ htmlInput: { maxLength: MAX_NOMINATION_LENGTH } }}
                  onChange={(e) => setNomination(e.target.value)}
                />
              </Box>

              <Box sx={NO_SELECT_SX} {...noCopyProps}>
                <Typography variant="body2" fontWeight="bold">
                  {t('rmet.task_write')}
                </Typography>
              </Box>

              {errorMsg && <Alert severity="error">{errorMsg}</Alert>}

              {/* A single free-text box: no placeholder, no character guidance,
                  no example — any more scaffold supplies what is being measured. */}
              <TextField
                fullWidth
                multiline
                minRows={8}
                size="small"
                value={brief}
                disabled={step === 'submitting'}
                slotProps={{ htmlInput: { maxLength: 4000 } }}
                // Pasting is rejected so the brief has to be written, not
                // brought in from elsewhere. Typing and editing are untouched;
                // only the paste and drop paths are closed.
                onPaste={(e) => {
                  e.preventDefault();
                  setPasteBlocked(true);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setPasteBlocked(true);
                }}
                error={pasteBlocked}
                helperText={pasteBlocked ? t('rmet.paste_blocked') : ' '}
                onChange={(e) => {
                  setPasteBlocked(false);
                  setBrief(e.target.value);
                }}
              />

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={step === 'submitting' || !nomination.trim() || !brief.trim()}
                loading={step === 'submitting'}
              >
                {t('rmet.task_finish')}
              </Button>
            </Box>
          )}

          {step === 'done' && score !== null && (
            <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={3}>
              <Typography variant="h6">{t('rmet.done_title')}</Typography>
              <Typography variant="subtitle1" mt={1}>
                {t('rmet_credits.result_title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('rmet_credits.result_score_label')}
              </Typography>
              {/* Raw score only: no per-item feedback, no high/low label. */}
              <Typography variant="h3" fontWeight="bold">
                {score} / 36
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" mt={1}>
                {t('rmet_credits.result_explanation')}
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {t('rmet_credits.result_non_diagnostic')}
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" mt={1}>
                {t('rmet.done_note')}
              </Typography>
              <Typography variant="caption" color="text.secondary" textAlign="center" mt={2}>
                {t('rmet_credits.result_credit')}
              </Typography>
              <RmetCreditsLink onOpen={openCredits} />
            </Box>
          )}

          <RmetCreditsDialog open={creditsOpen} onClose={() => setCreditsOpen(false)} />
        </Paper>
      </Box>
    </ThemeProvider>
  );
};
